<template>
  <DialogHeader>
    <template #right>
      <div
        v-if="!loading"
        class="font-medium text-green-900 cursor-pointer"
        @click="handleSave"
      >
        Save
      </div>
      <Icon
        v-else
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin"
      />
    </template>
    Change payment method
  </DialogHeader>
  <DialogContent>
    <div id="card" />
    <Message v-if="errorMessage" type="error" class="mt-5">
      {{ errorMessage }}
    </Message>
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      :disabled="loading"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      class="w-1/2 tablet-h:w-auto"
      primary
      :loading="loading"
      @click="handleSave"
    >
      Save Changes
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { createCardElement, getStripe } from '@/shared/services/stripe'
import type { Stripe, StripeCardElement } from '@stripe/stripe-js'
import {
  BillingQueryDocument,
  useUpdateBillingInformationMutation,
  type BillingQueryQuery,
} from '@/shared/graphql/legacy/generated'
import { UiButton, Message, Icon } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'

const { close } = useDialog()

const { mutate: updateBillingInformation } =
  useUpdateBillingInformationMutation()

const stripe = ref<Stripe | null>(null)
const card = ref<StripeCardElement | null>(null)
const loading = ref(false)
const errorMessage = ref('')

onMounted(async () => {
  stripe.value = await getStripe()
  if (!stripe.value) return

  card.value = createCardElement(stripe.value)
  card.value.mount('#card')
})

const handleSave = async () => {
  try {
    if (!stripe.value || !card.value) return

    loading.value = true
    errorMessage.value = ''

    const result = await stripe.value.createPaymentMethod({
      type: 'card',
      card: card.value,
    })

    if (result.error) {
      errorMessage.value = result.error.message || 'Something went wrong'
      loading.value = false
      return
    }

    await updateBillingInformation(
      { paymentMethodId: result.paymentMethod.id },
      {
        update(cache) {
          cache.updateQuery<BillingQueryQuery>(
            { query: BillingQueryDocument },
            (response) => {
              if (!response) return

              return {
                ...response,
                billing: {
                  ...response.billing,
                  paymentMethod: {
                    __typename: 'PaymentMethod',
                    card: {
                      __typename: 'Card',
                      last4: result.paymentMethod.card?.last4 || '',
                      type: result.paymentMethod.card?.brand || '',
                    },
                    requiresAction: null,
                  },
                },
              }
            }
          )
        },
      }
    )

    close()
  } catch (error) {
    console.error(error)

    if (error instanceof Error) {
      errorMessage.value = error.message
    }
  } finally {
    loading.value = false
  }
}
</script>

<style>
#card > .__PrivateStripeElement {
  width: 100%;
}
</style>
